import React from 'react';
import { Box, Button, Typography } from '@mui/material';
const DoubleArrowIcon =
  'https://assets.askhippo.co.za/Icons/common/DoubleArrow_White.svg';
import { useNavigate } from 'react-router-dom';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import {
  DEFAULT_CARD_MAX_WIDTH,
  LaunchPadHeroFeature,
  resolveLaunchPadHeroTitle,
} from './LaunchPadHeroShared';
import Image from '../../Image/Image';

const LaunchPadHeroMobileComponent = ({
  title,
  subtitle,
  ctaLabel,
  ctaRoute,
  ctaButtonSx = {},
  imageSrc,
  imageAlt = '',
  features = [],
  footnote,
  footnoteAlign = 'center',
  maxWidth = DEFAULT_CARD_MAX_WIDTH,
  cardSx = {},
  imageSx = {},
  featureGridSx = {},
  contentSx = {},
  onImageLoad = null,
}) => {
  const navigate = useNavigate();
  const resolvedTitle = resolveLaunchPadHeroTitle(title);

  const handleCtaClick = () => {
    if (!ctaRoute) return;
    navigate(ctaRoute);
  };

  const handleImageLoad = () => {
    if (typeof onImageLoad === 'function') {
      onImageLoad();
    }
  };

  return (
    <>
      <ScrollFadeWrapper
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box sx={{ width: '100%', maxWidth, mx: 'auto', ...cardSx }}>
          <Box
            component="header"
            role="banner"
            sx={{
              borderRadius: '24px',
              backgroundColor: '#FFFFFF',
              boxShadow: '0px 18px 40px rgba(22, 47, 59, 0.08)',
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                px: '16px',
                pt: '16px',
                pb: '50px',
                ...contentSx,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minWidth: 0,
                  ...imageSx,
                }}
                aria-hidden="true"
              >
                <ScrollFadeWrapper
                  delay={0.15}
                  startingOpacity={0.5}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Image
                    width={290}
                    height={290}
                    src={imageSrc}
                    alt={imageAlt}
                    loading="eager"
                    fetchPriority="high"
                    onLoad={handleImageLoad}
                    onError={handleImageLoad}
                    sx={{
                      width: '100%',
                      maxWidth: '290px',
                      height: 'auto',
                      display: 'block',
                      objectFit: 'contain',
                      pointerEvents: 'none',
                      userSelect: 'none',
                    }}
                  />
                </ScrollFadeWrapper>
              </Box>

              <Box
                sx={{
                  mt: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  minWidth: 0,
                }}
              >
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: 'Pally, Inter, sans-serif',
                    fontWeight: 700,
                    color: '#162F3B',
                    whiteSpace: 'pre-line',
                    letterSpacing: '-0.04em',
                    fontSize: '2.125rem',
                    lineHeight: '2.25rem',
                    maxWidth: '100%',
                  }}
                >
                  {resolvedTitle}
                </Typography>

                {subtitle ? (
                  <Typography
                    component="p"
                    sx={{
                      mt: '10px',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      color: '#162F3B',
                      whiteSpace: 'pre-line',
                      fontSize: '1rem',
                      lineHeight: '1.25rem',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {subtitle}
                  </Typography>
                ) : null}

                {ctaLabel ? (
                  <Button
                    type="button"
                    variant="contained"
                    disableElevation
                    onClick={handleCtaClick}
                    sx={{
                      mt: '16px',
                      minHeight: '44px',
                      px: '22px',
                      borderRadius: '999px',
                      backgroundColor: '#FF007A',
                      color: '#FFFFFF',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1rem',
                      lineHeight: 1,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#E6006D',
                      },
                      ...ctaButtonSx,
                    }}
                  >
                    {ctaLabel}
                    <img
                      src={DoubleArrowIcon}
                      alt="Double arrow icon"
                      width={18}
                      height={18}
                      style={{ display: 'block', marginLeft: '6px' }}
                    />
                  </Button>
                ) : null}
              </Box>
            </Box>

            {features.length > 0 ? (
              <Box
                sx={{
                  px: '40px',
                  pb: '20px',
                }}
              >
                <Box
                  sx={{
                    borderTop: '1px solid #E3E6E8',
                    pt: '18px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                      },
                      gap: '16px',
                      alignItems: 'start',
                      ...featureGridSx,
                    }}
                  >
                    {features.map((feature, index) => (
                      <LaunchPadHeroFeature
                        key={`${feature.label}-${index}`}
                        icon={feature.icon}
                        label={feature.label}
                        isDesktop={false}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            ) : null}
          </Box>
        </Box>
      </ScrollFadeWrapper>

      {footnote ? (
        <Typography
          sx={{
            mt: '8px',
            px: '40px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontStyle: 'normal',
            fontSize: '0.75rem',
            lineHeight: '1.25rem',
            letterSpacing: '0.01em',
            color: '#738289',
            textAlign: footnoteAlign,
          }}
        >
          {footnote}
        </Typography>
      ) : null}
    </>
  );
};

export default LaunchPadHeroMobileComponent;
